// Spec 045 Task 5: Pricing Matrix tab for the Admin Services Workspace.
// Shows all non-DRAFT variants in a grid (rows = cars, cols = variation combos).

import { useState, useMemo } from 'react';
import { api } from '../../services/api';
import { OfferingV2Response, VariantView, SupportedCarView, VariationView } from '../../types/offeringsV2';

interface Props {
  offering: OfferingV2Response;
  token: string;
  onChanged: () => Promise<void>;
}

type ActionMode = 'reject' | 'request-changes';

interface ReasonModalState {
  variantId: string;
  mode: ActionMode;
}

// Build a human-readable label for a supported car row.
function carLabel(car: SupportedCarView): string {
  if (car.carMakeName && car.carModelName) return `${car.carMakeName} ${car.carModelName}`;
  if (car.carMakeName) return `All ${car.carMakeName}`;
  if (car.carType) return `Any ${car.carType}`;
  return 'Unknown vehicle';
}

// Build a label for a variation combo given the set of variation value IDs.
function comboLabel(
  variationValueIds: string[],
  variations: VariationView[],
): string {
  if (variationValueIds.length === 0) return 'Flat price';
  const parts: string[] = [];
  for (const variation of variations) {
    for (const value of variation.values) {
      if (variationValueIds.includes(value.id)) {
        parts.push(`${variation.name}: ${value.label}`);
      }
    }
  }
  return parts.length > 0 ? parts.join(' · ') : variationValueIds.join(', ');
}

// Visible variants: exclude DRAFT (DELETED already filtered server-side).
function visibleVariants(variants: VariantView[]): VariantView[] {
  return variants.filter((v) => v.status !== 'DRAFT');
}

export default function PricingMatrixTab({ offering, token, onChanged }: Props) {
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [reasonModal, setReasonModal] = useState<ReasonModalState | null>(null);
  const [reasonText, setReasonText] = useState('');

  const variants = useMemo(() => visibleVariants(offering.variants ?? []), [offering.variants]);
  const pendingVariants = useMemo(() => variants.filter((v) => v.status === 'PENDING_APPROVAL'), [variants]);

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------

  const setBusy = (id: string, val: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      val ? next.add(id) : next.delete(id);
      return next;
    });
  };

  async function handleApproveOne(variantId: string) {
    setActionError(null);
    setBusy(variantId, true);
    try {
      await api.approveVariant(offering.id, variantId, token);
      await onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(variantId, false);
    }
  }

  async function handleApproveAllPending() {
    if (pendingVariants.length === 0) return;
    setActionError(null);
    setBulkBusy(true);
    try {
      await api.approveVariantsBulk(offering.id, pendingVariants.map((v) => v.id), token);
      await onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleApproveCar(carId: string | null) {
    const carVariants = pendingVariants.filter((v) => v.supportedCarId === carId);
    if (carVariants.length === 0) return;
    setActionError(null);
    try {
      await api.approveVariantsBulk(offering.id, carVariants.map((v) => v.id), token);
      await onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  }

  function openReason(variantId: string, mode: ActionMode) {
    setReasonModal({ variantId, mode });
    setReasonText('');
  }

  async function submitReason() {
    if (!reasonModal || !reasonText.trim()) return;
    setActionError(null);
    const { variantId, mode } = reasonModal;
    setBusy(variantId, true);
    try {
      if (mode === 'reject') {
        await api.rejectVariant(offering.id, variantId, reasonText.trim(), token);
      } else {
        await api.requestVariantChanges(offering.id, variantId, reasonText.trim(), token);
      }
      setReasonModal(null);
      await onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(variantId, false);
    }
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (variants.length === 0) {
    return (
      <div className="matrix-empty">
        No variants to display. Draft variants are hidden until the partner submits them.
      </div>
    );
  }

  const isCarSpecific = offering.isCarSpecific !== false;
  const supportedCars: Array<SupportedCarView | null> = isCarSpecific
    ? (offering.supportedCars ?? [])
    : [null]; // null = virtual "All vehicles" row

  const hasVariations = offering.variations && offering.variations.length > 0
    && variants.some((v) => v.variationValueIds.length > 0);

  // Flat-price (no variations) special layout
  if (!hasVariations) {
    return (
      <div className="matrix-container">
        <MatrixToolbar
          pendingCount={pendingVariants.length}
          onApproveAll={handleApproveAllPending}
          bulkBusy={bulkBusy}
        />
        {actionError && (
          <div className="admin-info-banner danger" style={{ margin: '8px 0' }}>
            {actionError}
          </div>
        )}
        <table className="matrix-flat-table">
          <thead>
            <tr>
              <th>{isCarSpecific ? 'Vehicle' : 'Pricing'}</th>
              <th>Price</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v) => {
              const car = isCarSpecific
                ? (offering.supportedCars ?? []).find((c) => c.id === v.supportedCarId)
                : null;
              const isBusy = busyIds.has(v.id);
              return (
                <tr key={v.id} className={`matrix-flat-row matrix-flat-row--${v.status.toLowerCase()}`}>
                  <td className="matrix-flat-row__car">
                    {car ? carLabel(car) : 'All vehicles'}
                  </td>
                  <td className="matrix-flat-row__price">
                    <span className={v.status === 'REJECTED' ? 'matrix-cell__price--struck' : ''}>
                      AED {v.price.toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <StatusBadge status={v.status} />
                  </td>
                  <td className="matrix-flat-row__actions">
                    <button
                      className="admin-icon-btn admin-icon-btn--approve"
                      title="Approve"
                      disabled={isBusy}
                      onClick={() => handleApproveOne(v.id)}
                    >
                      {'✓'}
                    </button>
                    <button
                      className="admin-icon-btn admin-icon-btn--reject"
                      title="Reject"
                      disabled={isBusy}
                      onClick={() => openReason(v.id, 'reject')}
                    >
                      {'✕'}
                    </button>
                    <button
                      className="admin-icon-btn admin-icon-btn--request"
                      title="Request changes"
                      disabled={isBusy}
                      onClick={() => openReason(v.id, 'request-changes')}
                    >
                      {'↺'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {reasonModal && (
          <ReasonModal
            mode={reasonModal.mode}
            text={reasonText}
            onChange={setReasonText}
            onSubmit={submitReason}
            onClose={() => setReasonModal(null)}
            busy={reasonModal ? busyIds.has(reasonModal.variantId) : false}
          />
        )}
      </div>
    );
  }

  // Per-car cards view — scales to N cars × M variations without rendering a Cartesian grid.
  return (
    <PricingMatrixCardsView
      offering={offering}
      variants={variants}
      pendingVariants={pendingVariants}
      supportedCars={supportedCars}
      busyIds={busyIds}
      bulkBusy={bulkBusy}
      actionError={actionError}
      reasonModal={reasonModal}
      reasonText={reasonText}
      handleApproveOne={handleApproveOne}
      handleApproveAllPending={handleApproveAllPending}
      handleApproveCar={handleApproveCar}
      openReason={openReason}
      submitReason={submitReason}
      setReasonModal={setReasonModal}
      setReasonText={setReasonText}
    />
  );
}

// ============================================================================
// Per-car cards view
// ============================================================================

type StatusFilter = 'all' | 'pending' | 'rejected' | 'changes';

interface CardsViewProps {
  offering: OfferingV2Response;
  variants: VariantView[];
  pendingVariants: VariantView[];
  supportedCars: Array<SupportedCarView | null>;
  busyIds: Set<string>;
  bulkBusy: boolean;
  actionError: string | null;
  reasonModal: ReasonModalState | null;
  reasonText: string;
  handleApproveOne: (variantId: string) => Promise<void>;
  handleApproveAllPending: () => Promise<void>;
  handleApproveCar: (carId: string | null) => Promise<void>;
  openReason: (variantId: string, mode: ActionMode) => void;
  submitReason: () => Promise<void>;
  setReasonModal: (s: ReasonModalState | null) => void;
  setReasonText: (s: string) => void;
}

function PricingMatrixCardsView({
  offering,
  variants,
  pendingVariants,
  supportedCars,
  busyIds,
  bulkBusy,
  actionError,
  reasonModal,
  reasonText,
  handleApproveOne,
  handleApproveAllPending,
  handleApproveCar,
  openReason,
  submitReason,
  setReasonModal,
  setReasonText,
}: CardsViewProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Per-card expansion state. Cards with pending / rejected / needs-changes
  // auto-expand by default; the user can collapse/expand manually.
  const [manuallyToggled, setManuallyToggled] = useState<Set<string>>(new Set());
  const [collapsedAuto, setCollapsedAuto] = useState<Set<string>>(new Set());

  // Pre-compute per-car summaries.
  const carRows = useMemo(() => {
    return supportedCars.map((car) => {
      const carId = car ? car.id : null;
      const carVariants = variants.filter((v) => v.supportedCarId === carId);
      const counts = {
        total: carVariants.length,
        pending: carVariants.filter((v) => v.status === 'PENDING_APPROVAL').length,
        active: carVariants.filter((v) => v.status === 'ACTIVE').length,
        rejected: carVariants.filter((v) => v.status === 'REJECTED').length,
        changes: carVariants.filter((v) => v.status === 'NEEDS_CHANGES').length,
      };
      const label = car ? carLabel(car) : 'All vehicles';
      return { car, carId, label, variants: carVariants, counts };
    });
  }, [supportedCars, variants]);

  // Apply search + status filter.
  const filteredCarRows = useMemo(() => {
    return carRows.filter((row) => {
      if (search.trim() && !row.label.toLowerCase().includes(search.trim().toLowerCase())) {
        return false;
      }
      if (statusFilter === 'pending' && row.counts.pending === 0) return false;
      if (statusFilter === 'rejected' && row.counts.rejected === 0) return false;
      if (statusFilter === 'changes' && row.counts.changes === 0) return false;
      return true;
    });
  }, [carRows, search, statusFilter]);

  const isExpanded = (key: string, autoExpand: boolean): boolean => {
    const wasToggled = manuallyToggled.has(key);
    if (autoExpand) return !collapsedAuto.has(key);
    return wasToggled;
  };

  const toggleExpand = (key: string, autoExpand: boolean) => {
    if (autoExpand) {
      // Was open by default → collapse it.
      setCollapsedAuto((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    } else {
      setManuallyToggled((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    }
  };

  return (
    <div className="matrix-container">
      <MatrixToolbar
        pendingCount={pendingVariants.length}
        onApproveAll={handleApproveAllPending}
        bulkBusy={bulkBusy}
      />

      {/* Search + status filter chips */}
      <div className="matrix-cards-controls">
        <input
          type="search"
          className="matrix-cards-search"
          placeholder={`Search ${carRows.length} cars…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="matrix-cards-chips">
          {(
            [
              ['all', `All (${carRows.length})`],
              ['pending', `Pending (${carRows.filter((r) => r.counts.pending > 0).length})`],
              ['rejected', `Rejected (${carRows.filter((r) => r.counts.rejected > 0).length})`],
              ['changes', `Changes (${carRows.filter((r) => r.counts.changes > 0).length})`],
            ] as Array<[StatusFilter, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              className={`matrix-cards-chip${statusFilter === key ? ' matrix-cards-chip--active' : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {actionError && (
        <div className="admin-info-banner danger" style={{ margin: '8px 0' }}>
          {actionError}
        </div>
      )}

      {filteredCarRows.length === 0 && (
        <div className="matrix-empty">
          No cars match the current filter.
        </div>
      )}

      <div className="matrix-cards-list">
        {filteredCarRows.map((row) => {
          const cardKey = row.carId ?? '__nocars__';
          const autoExpand = row.counts.pending > 0 || row.counts.rejected > 0 || row.counts.changes > 0;
          const expanded = isExpanded(cardKey, autoExpand);

          return (
            <PerCarCard
              key={cardKey}
              row={row}
              expanded={expanded}
              onToggle={() => toggleExpand(cardKey, autoExpand)}
              variations={offering.variations ?? []}
              busyIds={busyIds}
              onApproveOne={handleApproveOne}
              onApproveCar={() => handleApproveCar(row.carId)}
              onReject={(variantId) => openReason(variantId, 'reject')}
              onRequestChanges={(variantId) => openReason(variantId, 'request-changes')}
            />
          );
        })}
      </div>

      {reasonModal && (
        <ReasonModal
          mode={reasonModal.mode}
          text={reasonText}
          onChange={setReasonText}
          onSubmit={submitReason}
          onClose={() => setReasonModal(null)}
          busy={reasonModal ? busyIds.has(reasonModal.variantId) : false}
        />
      )}
    </div>
  );
}

// ── Per-car card ──────────────────────────────────────────────────────

interface PerCarCardProps {
  row: {
    car: SupportedCarView | null;
    carId: string | null;
    label: string;
    variants: VariantView[];
    counts: { total: number; pending: number; active: number; rejected: number; changes: number };
  };
  expanded: boolean;
  onToggle: () => void;
  variations: VariationView[];
  busyIds: Set<string>;
  onApproveOne: (variantId: string) => void;
  onApproveCar: () => void;
  onReject: (variantId: string) => void;
  onRequestChanges: (variantId: string) => void;
}

function PerCarCard({
  row,
  expanded,
  onToggle,
  variations,
  busyIds,
  onApproveOne,
  onApproveCar,
  onReject,
  onRequestChanges,
}: PerCarCardProps) {
  const { label, variants, counts } = row;

  // Per-status breakdown ("3 approved · 1 rejected"): a car with one rejected
  // variant among approved ones must not read as a rejected car — always show
  // the approved count alongside the problem counts.
  const allApproved = counts.active > 0 && counts.pending === 0 && counts.rejected === 0 && counts.changes === 0;
  const summaryBadges = [
    allApproved
      ? { key: 'a', label: 'All approved', color: '#16a34a' }
      : counts.active > 0 && { key: 'a', label: `${counts.active} approved`, color: '#16a34a' },
    counts.pending > 0 && { key: 'p', label: `${counts.pending} pending`, color: '#d97706' },
    counts.changes > 0 && { key: 'c', label: `${counts.changes} changes`, color: '#d97706' },
    counts.rejected > 0 && { key: 'r', label: `${counts.rejected} rejected`, color: '#dc2626' },
  ].filter(Boolean) as Array<{ key: string; label: string; color: string }>;

  return (
    <div className={`matrix-card${expanded ? ' matrix-card--expanded' : ''}`}>
      <div className="matrix-card__header" onClick={onToggle} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}>
        <span className="matrix-card__chevron">{expanded ? '▾' : '▸'}</span>
        <span className="matrix-card__label">{label}</span>
        <span className="matrix-card__count">{counts.total} variant{counts.total !== 1 ? 's' : ''}</span>
        <div className="matrix-card__summary">
          {summaryBadges.map((b) => (
            <span key={b.key} className="matrix-card__summary-badge" style={{ color: b.color, borderColor: b.color }}>
              {b.label}
            </span>
          ))}
        </div>
        {counts.pending > 0 && (
          <button
            className="matrix-card__approve-all"
            onClick={(e) => { e.stopPropagation(); onApproveCar(); }}
          >
            {'✓'} Approve {counts.pending}
          </button>
        )}
      </div>

      {expanded && (
        <div className="matrix-card__body">
          <table className="matrix-card-table">
            <thead>
              <tr>
                <th>Combination</th>
                <th>Price</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant) => {
                const isBusy = busyIds.has(variant.id);
                const isPending = variant.status === 'PENDING_APPROVAL';
                return (
                  <tr key={variant.id} className={`matrix-card-row matrix-card-row--${variant.status.toLowerCase()}`}>
                    <td>{comboLabel(variant.variationValueIds, variations)}</td>
                    <td>
                      <span className={variant.status === 'REJECTED' ? 'matrix-cell__price--struck' : ''}>
                        AED {variant.price.toLocaleString()}
                      </span>
                      {variant.durationOverrideMinutes != null && (
                        <span className="matrix-card-row__duration">
                          {' '}· {Math.round(variant.durationOverrideMinutes / 1440 * 10) / 10}d
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={variant.status} />
                      {variant.rejectionReason && (
                        <div className="matrix-card-row__reason">
                          {variant.rejectionReason}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isPending ? (
                        <>
                          <button
                            className="matrix-card-action-btn matrix-card-action-btn--approve"
                            disabled={isBusy}
                            onClick={() => onApproveOne(variant.id)}
                            title="Approve"
                          >
                            ✓
                          </button>
                          <button
                            className="matrix-card-action-btn matrix-card-action-btn--reject"
                            disabled={isBusy}
                            onClick={() => onReject(variant.id)}
                            title="Reject"
                          >
                            ✕
                          </button>
                          <button
                            className="matrix-card-action-btn matrix-card-action-btn--changes"
                            disabled={isBusy}
                            onClick={() => onRequestChanges(variant.id)}
                            title="Request changes"
                          >
                            ↺
                          </button>
                        </>
                      ) : (
                        <span style={{ color: 'var(--admin-text-muted)', fontSize: 12 }}>
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Sub-components
// ------------------------------------------------------------------

function MatrixToolbar({
  pendingCount,
  onApproveAll,
  bulkBusy,
}: {
  pendingCount: number;
  onApproveAll: () => void;
  bulkBusy: boolean;
}) {
  return (
    <div className="matrix-toolbar">
      <span className="matrix-toolbar__count">
        {pendingCount > 0
          ? `${pendingCount} variant${pendingCount !== 1 ? 's' : ''} pending review`
          : 'No variants pending review'}
      </span>
      <button
        className="matrix-toolbar__bulk admin-btn admin-btn-primary admin-btn-sm"
        disabled={pendingCount === 0 || bulkBusy}
        onClick={onApproveAll}
      >
        {'✓'} Approve all pending
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string }> = {
    ACTIVE: { label: 'Active', color: '#16a34a' },
    REJECTED: { label: 'Rejected', color: '#dc2626' },
    NEEDS_CHANGES: { label: 'Changes requested', color: '#d97706' },
    PENDING_APPROVAL: { label: 'Awaiting review', color: '#d97706' },
    DRAFT: { label: 'Draft', color: '#6b7280' },
  };
  const { label, color } = map[status] ?? { label: status, color: '#6b7280' };
  return (
    <span className="matrix-status-badge" style={{ color, borderColor: color }}>
      {label}
    </span>
  );
}

function ReasonModal({
  mode,
  text,
  onChange,
  onSubmit,
  onClose,
  busy,
}: {
  mode: ActionMode;
  text: string;
  onChange: (val: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  busy: boolean;
}) {
  return (
    <div
      className="admin-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="admin-modal admin-reason-modal">
        <header className="admin-modal-header">
          <h3>{mode === 'reject' ? 'Reject variant' : 'Request changes'}</h3>
          <button onClick={onClose} className="admin-modal-close">&times;</button>
        </header>
        <div className="admin-modal-body">
          <label>
            Reason *
            <textarea
              rows={4}
              value={text}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Visible to the partner."
            />
          </label>
        </div>
        <footer className="admin-modal-footer">
          <button className="admin-btn admin-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="admin-btn admin-btn-primary"
            onClick={onSubmit}
            disabled={!text.trim() || busy}
          >
            {mode === 'reject' ? 'Reject' : 'Request changes'}
          </button>
        </footer>
      </div>
    </div>
  );
}
